import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, User as UserIcon, Building2, Mail, Phone, ArrowRight, Sparkles } from "lucide-react";

const KIND_LABEL = {
  contact: "Contact",
  marketing_contact: "Marketing contact",
  lead: "Lead",
  client: "Client",
};

const KIND_TINT = {
  contact: "bg-sky-50 text-sky-700 border-sky-200",
  marketing_contact: "bg-violet-50 text-violet-700 border-violet-200",
  lead: "bg-amber-50 text-amber-800 border-amber-200",
  client: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const KIND_PATH = {
  contact: (id) => `/contacts?focus=${id}`,
  marketing_contact: (id) => `/contacts?focus=${id}`,
  lead: (id) => `/leads?focus=${id}`,
  client: (id) => `/clients/${id}`,
};

/**
 * Modal shown when a rep tries to create a new lead / contact / client whose
 * fields fuzzy-match an existing person across the CRM. Triggers from
 * `useDuplicateCheck` below.
 */
export function DuplicateContactDialog({ open, onOpenChange, matches, bestScore, onContinue, onUseExisting }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="duplicate-contact-dialog">
        <div className="p-6 pb-3">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              Similar {matches.length === 1 ? "contact" : "contacts"} found
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              We found <strong>{matches.length}</strong> existing record{matches.length === 1 ? "" : "s"} that
              look{matches.length === 1 ? "s" : ""} like the person you're adding{bestScore >= 95 ? " — almost a perfect match" : ""}.
              Either link to an existing record, or create a fresh one (e.g. when adding a second lead/opportunity for the same person).
            </p>
          </DialogHeader>
        </div>

        <div className="space-y-2.5 px-6">
          {matches.slice(0, 5).map((m) => (
            <div
              key={`${m.kind}-${m.id}`}
              className="rounded-2xl border border-border/60 p-3.5 hover:bg-muted/40 transition"
              data-testid={`duplicate-match-${m.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-2xl bg-muted/50 grid place-items-center shrink-0">
                  {m.kind === "client" ? <Building2 className="h-5 w-5 text-muted-foreground" /> : <UserIcon className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate" data-testid={`duplicate-match-name-${m.id}`}>{m.label}</p>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${KIND_TINT[m.kind] || ""}`}>
                      {KIND_LABEL[m.kind] || m.kind}
                    </Badge>
                  </div>
                  {m.designation && <p className="text-xs text-muted-foreground">{m.designation}</p>}
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
                    {m.company_name && m.kind !== "client" && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{m.company_name}</span>}
                    {m.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{m.phone}</span>}
                    {m.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</span>}
                  </div>
                  {m.reasons?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.reasons.map((r, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200">
                          <Sparkles className="h-2.5 w-2.5 inline mr-1" />{r}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-xl font-extrabold tabular-nums" data-testid={`duplicate-match-score-${m.id}`}>{m.score}%</p>
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">match</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <Link
                  to={KIND_PATH[m.kind]?.(m.id) || "#"}
                  className="text-[11px] underline text-muted-foreground hover:text-foreground"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`duplicate-match-open-${m.id}`}
                >
                  Open in new tab
                </Link>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-2xl h-8 text-xs"
                  onClick={() => onUseExisting?.(m)}
                  data-testid={`duplicate-match-use-${m.id}`}
                >
                  It's the same — use this <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="sticky bottom-0 bg-card border-t border-border/60 px-6 py-3 mt-3 flex-row justify-between items-center sm:justify-between gap-2 z-10">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-2xl"
            data-testid="duplicate-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onContinue}
            className="rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground"
            data-testid="duplicate-create-anyway-button"
          >
            Create new anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook to wrap any form's submit handler with a duplicate check.
 *
 *   const dup = useDuplicateCheck();
 *   const submit = () => dup.guard({ name, email, phone, company_name }, async () => {
 *     await api.post("/leads", form);
 *   });
 *
 * Render `<dup.Dialog />` somewhere in your component tree.
 */
export function useDuplicateCheck(targetKind = null) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [bestScore, setBestScore] = useState(0);
  const pendingProceedRef = useRef(null);
  const pendingMatchRef = useRef(null);

  const guard = useCallback(async (candidate, proceed) => {
    // Cheap pre-check — if there's literally nothing to match on, just save.
    if (!candidate?.name && !candidate?.email && !candidate?.phone) {
      return proceed();
    }
    try {
      const { data } = await api.post("/contacts/check-duplicates", {
        name: candidate.name,
        email: candidate.email,
        phone: candidate.phone,
        company_name: candidate.company_name,
        target_kind: targetKind,
      });
      if (data?.matches?.length) {
        setMatches(data.matches);
        setBestScore(data.best_score || 0);
        pendingProceedRef.current = proceed;
        setOpen(true);
        return;
      }
    } catch {
      // Network/duplicate check fail — never block the rep, just fall through.
    }
    return proceed();
  }, [targetKind]);

  const onContinue = useCallback(() => {
    setOpen(false);
    const fn = pendingProceedRef.current;
    pendingProceedRef.current = null;
    pendingMatchRef.current = null;
    if (fn) fn();
  }, []);

  const onUseExisting = useCallback((match) => {
    setOpen(false);
    pendingProceedRef.current = null;
    pendingMatchRef.current = match;
    // Open the matched record in a new tab so the rep can verify / edit
    // without losing the half-typed form.
    const path = KIND_PATH[match.kind]?.(match.id);
    if (path && typeof window !== "undefined") {
      window.open(path, "_blank");
    }
  }, []);

  // Pre-bound dialog so callers don't have to pass props every time.
  const DialogEl = useCallback(
    () => (
      <DuplicateContactDialog
        open={open}
        onOpenChange={setOpen}
        matches={matches}
        bestScore={bestScore}
        onContinue={onContinue}
        onUseExisting={onUseExisting}
      />
    ),
    [open, matches, bestScore, onContinue, onUseExisting],
  );

  useEffect(() => {
    if (!open) {
      pendingProceedRef.current = null;
      pendingMatchRef.current = null;
    }
  }, [open]);

  return { guard, Dialog: DialogEl };
}
